import { LightningElement, api, wire } from "lwc";
import {
  LearningFragment,
  SearchFilterInputType,
  SearchSortType,
} from "@devforce/trailhead-client-gql";
import { EffectAdapter } from "common/effectAdapter";
import { LabelsAdapterValue } from "common/labelsAdapter";
import { CollectionConfig, createConfig } from "common/contentCollection";
import { logger } from "@devforce/trailhead-client-logger";
import { gql } from "common/graphqlAdapter";
import { ContentSummary } from "tds/contentSummary";
import {
  LearningCollectionCard,
  LearningCollectionQuery,
} from "lx/learningCollectionQuery";
import { UnifiedCollectionQuery } from "lx/unifiedCollectionQuery";
import { SharedLabelsAdapter } from "lx/sharedLabels";
import { BooleanAttr, attrToBool } from "common/helpers";

export type QueryParamsType = {
  limit?: number | undefined;
  query: string;
  filters: SearchFilterInputType;
  sort?: SearchSortType | null | undefined;
  tab?: string | null | undefined;
};

const queryConfigMap = {
  search: {
    operation: "LxSearch",
    field: "search",
  },
  index: {
    operation: "LxIndexPage",
    field: "indexPageSearch",
  },
};

export default class
  extends LightningElement
  implements LearningCollectionCard
{
  @api config: CollectionConfig | null = null;
  @api variant: "index" | "search" = "search";
  @api queryParams: QueryParamsType = {
    query: "lightning",
    filters: {
      dynamic: [],
      type: { eq: [] },
      operator: null,
      progress: null,
    },
    sort: null,
    tab: null,
  };
  @api pageLocation: string = "";
  @api
  get unifiedQueryModeEnabled() {
    return this._unifiedQueryModeEnabled;
  }

  set unifiedQueryModeEnabled(value: BooleanAttr) {
    this._unifiedQueryModeEnabled = attrToBool(value);
  }

  queryMode: "append" | null = "append";
  private _unifiedQueryModeEnabled = false;
  private query: LearningCollectionQuery | UnifiedCollectionQuery | null = null;

  private get configWithDefaults(): CollectionConfig {
    return createConfig(this.config, {
      card: {
        ctaLabel: this.labels.shared.cta,
      },
      limit: 25,
      state: {
        loading: {
          stencilCount: 10,
        },
      },
    });
  }

  private get fieldName() {
    const { variant } = this;
    return queryConfigMap[variant].field || "search";
  }

  private get gql() {
    return gql`
    query ${queryConfigMap[this.variant].operation}(
      $query: String!
      $first: Int = 10
      $before: String
      $after: String
      $sort: SearchSort
      $filter: SearchFilterInput
      $searchAnalyticsPayload: String
    ) {
      ${this.fieldName}(
        criteria: { text: $query, sort: $sort }
        filter: $filter
        first: $first
        before: $before
        after: $after
        searchAnalyticsPayload: $searchAnalyticsPayload
      ) {
        searchAnalyticsPayload
        totalCount
        __typename
        edges {
          __typename
          cursor
          node {
            clickAnalyticsPayload
            learning {
              ...Learning
            }
          }
        }
        pageInfo {
          hasPreviousPage
          hasNextPage
          startCursor
          endCursor
        }
      }
    }
    ${LearningFragment}
  `;
  }

  get variables() {
    const { limit, query, sort, filters, tab } = this.queryParams;
    return {
      first: limit,
      query,
      sort,
      filter: filters,
      tab: this.unifiedQueryModeEnabled ? tab : null,
      searchAnalyticsPayload: this.query?.searchAnalyticsPayload || undefined,
    };
  }

  @wire(EffectAdapter, {
    queryParams: "$queryParams",
  })
  private onqueryParamsChange() {
    const oldState = {
      ...this.query?.getQueryOptions().variables,
      searchAnalyticsPayload: undefined,
    };
    const newState = {
      ...this.variables,
      searchAnalyticsPayload: undefined,
    };

    if (JSON.stringify(oldState) != JSON.stringify(newState)) {
      this.query = this.collectionQuery();
    }
  }

  @wire(SharedLabelsAdapter)
  private labels: { shared: LabelsAdapterValue } = null!;

  passEventUp(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  private handleNavigate(e: CustomEvent<ContentSummary>) {
    const searchAnalyticsPayload = this.query?.searchAnalyticsPayload;
    if (searchAnalyticsPayload) {
      const clickPayload = this.query?.getClickAnalyticsEventData(e.detail.id);
      void this.uploadSearchAnalytics(searchAnalyticsPayload, clickPayload);
    }
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  private async uploadSearchAnalytics(
    searchPayload: string,
    clickPayload?: string
  ) {
    let { getClient, gql } = await import("@devforce/trailhead-client-apollo");
    try {
      await getClient().mutate({
        mutation: gql`
          mutation submitSearchAnalytic(
            $eventType: SearchEvent!
            $searchAnalyticsPayload: String!
            $clickAnalyticsPayload: String
          ) {
            submitSearchAnalytic(
              input: {
                eventType: $eventType
                clickAnalyticsPayload: $clickAnalyticsPayload
                searchAnalyticsPayload: $searchAnalyticsPayload
              }
            ) {
              __typename
              completed
            }
          }
        `,
        variables: {
          eventType: "CLICK",
          searchAnalyticsPayload: searchPayload,
          clickAnalyticsPayload: clickPayload,
        },
      });
    } catch (e) {
      logger.error(e as string);
    }
  }

  private collectionQuery(): LearningCollectionQuery | UnifiedCollectionQuery {
    if (this.unifiedQueryModeEnabled) {
      return new UnifiedCollectionQuery({
        variables: this.variables,
      });
    } else {
      return new LearningCollectionQuery({
        collectionKey: this.fieldName,
        query: this.gql,
        variables: this.variables,
      });
    }
  }
}
